import { useState } from 'react';
import { Link } from 'react-router-dom';
import { footerData } from '@/mocks/home';

const SUBSCRIBE_FORM_URL = 'http://emenculture.com/';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubscribeStatus('submitting');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());

      const response = await fetch(SUBSCRIBE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('订阅成功！感谢您的关注。');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setSubscribeMsg(parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试');
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请稍后重试');
    }
  };

  return (
    <footer className="bg-foreground-950 rounded-t-[40px] mx-4 md:mx-6 mt-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h2 className="font-heading text-4xl md:text-5xl font-light text-background-50 tracking-tight">
              {footerData.subscribeTitle}
            </h2>
            <p className="mt-3 text-sm text-foreground-400 leading-relaxed">
              {footerData.subscribeDesc}
            </p>
            <form onSubmit={handleSubscribe} className="mt-8 flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={footerData.subscribePlaceholder}
                className="flex-1 bg-transparent border-b border-foreground-600 text-background-50 text-sm py-3 px-1 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-500"
                required
              />
              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'submitting'}
                className="bg-background-50 text-foreground-950 px-8 py-3 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {subscribeStatus === 'submitting' ? '订阅中...' : footerData.subscribeButton}
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {subscribeMsg && (
              <p className={`mt-3 text-sm ${subscribeStatus === 'success' ? 'text-accent-400' : 'text-primary-400'}`}>
                {subscribeMsg}
              </p>
            )}
          </div>

          <div className="md:col-span-3">
            <p className="text-sm font-semibold text-background-50 mb-5">{footerData.navTitle}</p>
            <ul className="flex flex-col gap-3">
              {footerData.navLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline underline-offset-4 decoration-foreground-600 hover:decoration-background-50 cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-sm font-semibold text-background-50 mb-5">{footerData.contactTitle}</p>
            <p className="text-sm text-foreground-400 mb-2">{footerData.contactPhone}</p>
            <p className="text-sm text-foreground-400 mb-8">{footerData.contactEmail}</p>
            <p className="text-sm font-semibold text-background-50 mb-2">{footerData.addressTitle}</p>
            <p className="text-sm text-foreground-400">{footerData.addressLine1}</p>
            <p className="text-sm text-foreground-400">{footerData.addressLine2}</p>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-foreground-700/40 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            {footerData.socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                rel="nofollow"
                className="text-foreground-400 hover:text-background-50 transition-colors underline underline-offset-4 decoration-foreground-600 hover:decoration-background-50 cursor-pointer"
              >
                <span className="text-sm">{social.label}</span>
              </a>
            ))}
          </div>
          <p className="text-xs text-foreground-500">{footerData.copyright}</p>
          <div className="flex items-center gap-6">
            {footerData.policyLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                rel="nofollow"
                className="text-xs text-foreground-400 hover:text-background-50 transition-colors underline underline-offset-4 decoration-foreground-600 hover:decoration-background-50 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}